import {
  forwardRef,
  useId,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react'
import { cn } from '@/lib/utils'

const CONTROL =
  'w-full rounded-sm border border-border-strong bg-surface px-3 py-2 text-body text-text ' +
  'placeholder:text-text-muted transition-colors hover:border-n-400 ' +
  'disabled:cursor-not-allowed disabled:bg-surface-tint disabled:opacity-60 ' +
  'aria-[invalid=true]:border-danger'

export function FieldShell({
  label,
  hint,
  error,
  required,
  htmlFor,
  children,
  className,
}: {
  label?: ReactNode
  hint?: ReactNode
  error?: string | undefined
  required?: boolean
  htmlFor?: string
  children: ReactNode
  className?: string
}) {
  return (
    <div className={cn('flex flex-col gap-1', className)}>
      {label && (
        <label htmlFor={htmlFor} className="text-meta font-medium text-text">
          {label}
          {required && (
            <span aria-hidden className="ml-1 text-danger">
              *
            </span>
          )}
        </label>
      )}
      {children}
      {error ? (
        <p className="text-meta text-danger">{error}</p>
      ) : hint ? (
        <p className="text-meta text-text-muted">{hint}</p>
      ) : null}
    </div>
  )
}

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: ReactNode
  hint?: ReactNode
  error?: string | undefined
  leadingIcon?: ReactNode
  trailingSlot?: ReactNode
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { label, hint, error, leadingIcon, trailingSlot, className, id, required, ...rest },
  ref,
) {
  const generatedId = useId()
  const inputId = id ?? generatedId
  const describedBy = hint || error ? `${inputId}-description` : undefined

  return (
    <FieldShell
      label={label}
      hint={hint}
      error={error}
      required={required}
      htmlFor={inputId}
      className={className}
    >
      <div className="relative flex items-center">
        {leadingIcon && (
          <span aria-hidden className="pointer-events-none absolute left-3 text-text-muted">
            {leadingIcon}
          </span>
        )}
        <input
          ref={ref}
          id={inputId}
          required={required}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy}
          className={cn(CONTROL, leadingIcon && 'pl-10', trailingSlot && 'pr-10')}
          {...rest}
        />
        {trailingSlot && <span className="absolute right-2 flex items-center">{trailingSlot}</span>}
      </div>
    </FieldShell>
  )
})

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: ReactNode
  hint?: ReactNode
  error?: string | undefined
  /** Renders a live counter that warns at 90% and errors at the limit (§12.8). */
  maxLength?: number
  value?: string
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { label, hint, error, className, id, required, maxLength, value, ...rest },
  ref,
) {
  const generatedId = useId()
  const textareaId = id ?? generatedId
  const length = value?.length ?? 0
  const warn = maxLength !== undefined && length >= maxLength * 0.9
  const over = maxLength !== undefined && length >= maxLength

  return (
    <FieldShell
      label={label}
      hint={hint}
      error={error}
      required={required}
      htmlFor={textareaId}
      className={className}
    >
      <textarea
        ref={ref}
        id={textareaId}
        required={required}
        value={value}
        aria-invalid={error ? true : undefined}
        className={cn(CONTROL, 'min-h-28 resize-y')}
        {...rest}
      />
      {maxLength !== undefined && (
        <p
          aria-live="polite"
          className={cn(
            'self-end text-meta tabular',
            over ? 'text-danger' : warn ? 'text-warning' : 'text-text-muted',
          )}
        >
          {length} / {maxLength}
        </p>
      )}
    </FieldShell>
  )
})

export interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  label?: ReactNode
  hint?: ReactNode
  error?: string | undefined
}

/**
 * A native <select>. Radix's Select is used where a rich popover is needed; for
 * a plain list the native control is smaller, fully keyboard-operable and sizes
 * itself to the longest translated option for free.
 */
export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { label, hint, error, className, id, required, children, ...rest },
  ref,
) {
  const generatedId = useId()
  const selectId = id ?? generatedId
  return (
    <FieldShell
      label={label}
      hint={hint}
      error={error}
      required={required}
      htmlFor={selectId}
      className={className}
    >
      <select
        ref={ref}
        id={selectId}
        required={required}
        aria-invalid={error ? true : undefined}
        className={cn(CONTROL, 'pr-8')}
        {...rest}
      >
        {children}
      </select>
    </FieldShell>
  )
})

export function Checkbox({
  label,
  count,
  disabled,
  className,
  ...rest
}: InputHTMLAttributes<HTMLInputElement> & { label: ReactNode; count?: number }) {
  const id = useId()
  return (
    <div className={cn('flex items-start gap-2', disabled && 'opacity-50', className)}>
      <input
        id={id}
        type="checkbox"
        disabled={disabled}
        className="mt-[3px] size-4 shrink-0 accent-itpu-teal"
        {...rest}
      />
      <label htmlFor={id} className={cn('flex-1 text-body', !disabled && 'cursor-pointer')}>
        {label}
      </label>
      {count !== undefined && <span className="text-meta tabular text-text-muted">{count}</span>}
    </div>
  )
}
